import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Card, CardContent, Alert, Grid, Skeleton, Button, Select, MenuItem, TextField, FormControl, InputLabel, Divider, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper, Stack, Chip, Avatar } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MainLayout from '../layouts/MainLayout';
import { getAggregatedReport } from '../services/mwcService';

// Icons
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DownloadIcon from '@mui/icons-material/Download';
import StoreIcon from '@mui/icons-material/Store';

const StatWidget = ({ title, value, icon, color, loading }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden', boxShadow: 3 }}>
    <Box sx={{ 
        position: 'absolute', 
        top: -10, 
        right: -10, 
        opacity: 0.1, 
        transform: 'rotate(-10deg) scale(2.5)',
        color: color 
    }}>
        {icon}
    </Box>
    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 56, height: 56, mr: 2 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="body2" color="text.secondary" fontWeight="medium">{title}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {loading ? <Skeleton width={120} /> : value}
            </Typography>
        </Box>
    </CardContent>
  </Card>
);

const AggregatedReportPage = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getAggregatedReport(selectedMonth, selectedYear);

        // Client-side Fallback & Enrichment
        if ((!data.rantingDetails || Object.keys(data.rantingDetails).length === 0) && data.transactions) {
            data.rantingDetails = {};
            data.transactions.forEach(tx => {
                if (tx.rantingId || tx.ranting) {
                    const rId = tx.rantingId || (tx.ranting ? tx.ranting.id : 'unknown');
                    const rName = tx.ranting ? tx.ranting.name : `Ranting #${rId}`;
                    if (!data.rantingDetails[rId]) {
                        data.rantingDetails[rId] = { name: rName, transactionCount: 0, totalPendapatan: 0, totalBeban: 0, labaRugi: 0 };
                    }
                    const detail = data.rantingDetails[rId];
                    const amount = parseFloat(tx.jumlah) || 0;
                    detail.transactionCount++;
                    if (tx.jenis === 'PEMASUKAN') detail.totalPendapatan += amount;
                    else detail.totalBeban += amount;
                }
            });
            Object.values(data.rantingDetails).forEach(r => { r.labaRugi = r.totalPendapatan - r.totalBeban; });
        }
        setReportData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedMonth, selectedYear]);

  // Calculations
  const totalGabungan = useMemo(() => {
    if (!reportData) return { pendapatan: 0, beban: 0, labaRugi: 0 };
    const mwcP = parseFloat(reportData.mwc?.totalPendapatan) || 0;
    const mwcB = parseFloat(reportData.mwc?.totalBeban) || 0;
    const rantingP = parseFloat(reportData.ranting?.totalPendapatan) || 0;
    const rantingB = parseFloat(reportData.ranting?.totalBeban) || 0;
    const p = mwcP + rantingP;
    const b = mwcB + rantingB;
    return { pendapatan: p, beban: b, labaRugi: p - b };
  }, [reportData]);

  // Sorting Ranting by Income (Highest First)
  const sortedRantings = useMemo(() => {
    if (!reportData?.rantingDetails) return [];
    return Object.values(reportData.rantingDetails).sort((a, b) => b.totalPendapatan - a.totalPendapatan);
  }, [reportData]);

  // Chart Data Preparation
  const pieData = useMemo(() => {
    if (!reportData) return [];
    const mwcP = parseFloat(reportData.mwc?.totalPendapatan) || 0;
    const rantingP = parseFloat(reportData.ranting?.totalPendapatan) || 0;
    return [
        { id: 0, value: mwcP, label: 'Internal MWC', color: '#1976d2' },
        { id: 1, value: rantingP, label: 'Akumulasi Ranting', color: '#9c27b0' },
    ];
  }, [reportData]);

  const generatePdf = (data, transactions, fileName, title) => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.setTextColor('#1565c0');
    doc.text(title, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor('#666');
    doc.text(`Dicetak pada: ${new Date().toLocaleDateString('id-ID')}`, 14, 28);
    
    // Summary Table Logic (Same as before but simplified for brevity)
    const mwcP = parseFloat(data.mwc?.totalPendapatan)||0; const mwcB = parseFloat(data.mwc?.totalBeban)||0;
    const rantingP = parseFloat(data.ranting?.totalPendapatan)||0; const rantingB = parseFloat(data.ranting?.totalBeban)||0;

    const summaryBody = [
        ['Internal MWC', `Rp${mwcP.toLocaleString('id-ID')}`, `Rp${mwcB.toLocaleString('id-ID')}`, `Rp${(mwcP - mwcB).toLocaleString('id-ID')}`],
        ['Akumulasi Ranting', `Rp${rantingP.toLocaleString('id-ID')}`, `Rp${rantingB.toLocaleString('id-ID')}`, `Rp${(rantingP - rantingB).toLocaleString('id-ID')}`],
        [{ content: 'Total Gabungan', styles: { fontStyle: 'bold' } }, 
         { content: `Rp${(mwcP + rantingP).toLocaleString('id-ID')}`, styles: { fontStyle: 'bold' } }, 
         { content: `Rp${(mwcB + rantingB).toLocaleString('id-ID')}`, styles: { fontStyle: 'bold' } }, 
         { content: `Rp${((mwcP + rantingP) - (mwcB + rantingB)).toLocaleString('id-ID')}`, styles: { fontStyle: 'bold', fillColor: '#e0e0e0' } }
        ]
    ];
    autoTable(doc, { head: [['Kategori', 'Pemasukan', 'Pengeluaran', 'Saldo Bersih']], body: summaryBody, startY: 35, theme: 'grid', headStyles: { fillColor: [21, 101, 192] } });

    // Ranting Details
    if (data.rantingDetails && Object.keys(data.rantingDetails).length > 0) {
        doc.text("Rincian Kinerja Per Ranting (Top Performers)", 14, doc.lastAutoTable.finalY + 12);
        const rantingBody = Object.values(data.rantingDetails)
            .sort((a,b) => b.totalPendapatan - a.totalPendapatan)
            .map(r => [r.name, r.transactionCount, `Rp${parseFloat(r.totalPendapatan).toLocaleString('id-ID')}`, `Rp${parseFloat(r.totalBeban).toLocaleString('id-ID')}`, `Rp${(parseFloat(r.totalPendapatan) - parseFloat(r.totalBeban)).toLocaleString('id-ID')}`]);
        autoTable(doc, { head: [['Nama Ranting', 'Jml Tx', 'Pemasukan', 'Pengeluaran', 'Saldo']], body: rantingBody, startY: doc.lastAutoTable.finalY + 16, theme: 'striped', headStyles: { fillColor: [156, 39, 176] } });
    }
    
    doc.save(fileName);
  };

  const handleDownloadPdf = () => {
    if (!reportData) return;
    const monthName = selectedMonth === 0 ? 'Tahunan' : new Date(0, selectedMonth - 1).toLocaleString('id-ID', { month: 'long' });
    const fileName = `Laporan_Agregat_${monthName}_${selectedYear}.pdf`;
    generatePdf(reportData, reportData.transactions, fileName, `Laporan Agregat - ${monthName} ${selectedYear}`);
  };

  if (error) return <MainLayout><Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box></MainLayout>;

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        
        {/* HEADER & FILTER */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0' }}>
            <Box>
                <Typography variant="h5" fontWeight="bold" color="primary.main">Dashboard Laporan Agregat</Typography>
                <Typography variant="body2" color="text.secondary">Analisis kinerja keuangan MWC dan Ranting secara real-time.</Typography>
            </Box>
            <Stack direction={{xs:'column', sm:'row'}} spacing={2} sx={{ mt: {xs:2, md:0} }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Periode Bulan</InputLabel>
                    <Select value={selectedMonth} label="Periode Bulan" onChange={(e) => setSelectedMonth(e.target.value)}>
                        <MenuItem value={0}>Semua (Tahunan)</MenuItem>
                        {[...Array(12).keys()].map(m => (
                            <MenuItem key={m + 1} value={m + 1}>{new Date(0, m).toLocaleString('id-ID', { month: 'long' })}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField size="small" label="Tahun" type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} sx={{ width: 100 }} />
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadPdf} sx={{ borderRadius: 2 }}>Export PDF</Button>
            </Stack>
        </Paper>

        {/* HERO STATS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <StatWidget 
              title="Total Pemasukan Gabungan" 
              value={`Rp${(totalGabungan.pendapatan || 0).toLocaleString('id-ID')}`} 
              icon={<TrendingUpIcon fontSize="large" />} 
              color="#2e7d32" // Green
              loading={loading} 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatWidget 
              title="Total Pengeluaran Gabungan" 
              value={`Rp${(totalGabungan.beban || 0).toLocaleString('id-ID')}`} 
              icon={<TrendingDownIcon fontSize="large" />} 
              color="#d32f2f" // Red
              loading={loading} 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatWidget 
              title="Saldo Bersih Saat Ini" 
              value={`Rp${(totalGabungan.labaRugi || 0).toLocaleString('id-ID')}`} 
              icon={<AccountBalanceWalletIcon fontSize="large" />} 
              color="#1976d2" // Blue
              loading={loading} 
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
            {/* LEFT COLUMN: RANTING TABLE */}
            <Grid item xs={12} lg={8}>
                <Card sx={{ boxShadow: 3, height: '100%', borderRadius: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <StoreIcon color="secondary" />
                            <Typography variant="h6" fontWeight="bold">Kinerja Per Ranting (Top Performers)</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Diurutkan berdasarkan pemasukan tertinggi.
                        </Typography>

                        <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Nama Ranting</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Tx</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Masuk</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Keluar</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Saldo</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={5}><Skeleton /></TableCell></TableRow>)
                                    ) : sortedRantings.length === 0 ? (
                                        <TableRow><TableCell colSpan={5} align="center">Belum ada data ranting.</TableCell></TableRow>
                                    ) : (
                                        sortedRantings.map((r, idx) => (
                                            <TableRow key={r.name} hover>
                                                <TableCell sx={{ fontWeight: 'medium' }}>
                                                    {idx < 3 && <Chip label={`#${idx+1}`} size="small" color="secondary" sx={{ mr: 1, height: 20, fontSize: '0.65rem' }} />}
                                                    {r.name}
                                                </TableCell>
                                                <TableCell align="center">{r.transactionCount}</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>Rp{parseFloat(r.totalPendapatan).toLocaleString('id-ID')}</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary' }}>Rp{parseFloat(r.totalBeban).toLocaleString('id-ID')}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rp{(parseFloat(r.totalPendapatan) - parseFloat(r.totalBeban)).toLocaleString('id-ID')}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* RIGHT COLUMN: CONTRIBUTION ANALYSIS */}
            <Grid item xs={12} lg={4}>
                <Card sx={{ boxShadow: 3, height: '100%', borderRadius: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.light', borderRadius: '50%', color: 'white' }}>%</Box>
                            <Typography variant="h6" fontWeight="bold">Proporsi Pemasukan</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                            Perbandingan kontribusi Internal MWC vs Akumulasi Ranting.
                        </Typography>

                        <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {loading ? <Skeleton variant="circular" width={200} height={200} /> : (
                                (totalGabungan.pendapatan === 0) ? 
                                <Typography color="text.secondary">Belum ada data pemasukan.</Typography> :
                                <PieChart
                                    series={[
                                        {
                                            data: pieData,
                                            innerRadius: 30,
                                            outerRadius: 100,
                                            paddingAngle: 2,
                                            cornerRadius: 5,
                                            startAngle: -90,
                                            endAngle: 270,
                                            cx: 140, // Adjust center
                                        }
                                    ]}
                                    width={350}
                                    height={200}
                                    slotProps={{
                                        legend: {
                                            direction: 'row',
                                            position: { vertical: 'bottom', horizontal: 'middle' },
                                            padding: 0,
                                        },
                                    }}
                                />
                            )}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Summary List */}
                         <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">Internal MWC</Typography>
                                <Typography variant="body2" fontWeight="bold" color="primary.main">
                                    Rp{(parseFloat(reportData?.mwc?.totalPendapatan)||0).toLocaleString('id-ID')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Total Ranting</Typography>
                                <Typography variant="body2" fontWeight="bold" color="secondary.main">
                                    Rp{(parseFloat(reportData?.ranting?.totalPendapatan)||0).toLocaleString('id-ID')}
                                </Typography>
                            </Box>
                         </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default AggregatedReportPage;